import React from 'react';
import moment from 'moment';
import { styled } from '@mui/material';

import {
    Box,
    Button,
    Checkbox,
    DatePicker,
    Divider,
    Dropdown,
    Icon,
    IconButton,
    MenuItem,
    RichTextArea,
    Select,
    SplitButton,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';
import { useViewport } from 'contexts/viewport';
import ResourceSection from 'components/organisms/PeerEvaluation/ResourceSection';
import RubricTableWrapper from 'components/organisms/PeerEvaluation/RubricTableWrapper';
import { AlertIcon } from './PeerEvaluationComponents';
import { useAuth } from 'contexts/auth';

const CreatePeerEvaluationMobile = ({
    auth,
    buttonLoading,
    errors,
    form,
    hasChange,

    renderLTIAssignmentsMenuItem,

    handleInputs,
    handleBackButton,
    rubricData,
    setRubricData,
    submissions,
    handleDeleteFile,
    setResourcesModal,
    getSplitButtons,
    getCreatedOnText,
    setToolTipDialog,
    renderOptionDropDown,
    handleDropDown,
    handleSplitButton,

    FORM_KEYS,
}) => {
    const { isLTI } = useAuth();
    const { device } = useViewport();

    return (
        <OutterBox>
            <DesktopBox device={device}>
                <MainBox device={device}>
                    <FormStack spacing={7}>
                        <Stack spacing={4}>
                            <Stack alignItems={'center'} direction={'row'} spacing={7}>
                                <IconButton
                                    aria-label={'back'}
                                    kind={'arrowLeft'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={() => handleBackButton()}
                                />
                                <TitleTextInput
                                    data-cy={'peer-evaluation-title-input'}
                                    value={form?.[FORM_KEYS.TITLE]}
                                    error={errors?.[FORM_KEYS.TITLE]}
                                    placeholder={'Peer Evaluation Title'}
                                    onChange={(e) => handleInputs(FORM_KEYS.TITLE, e.target.value)}
                                />
                            </Stack>
                            <Text variant={'p1'} color={'light'}>
                                {getCreatedOnText(form)}
                            </Text>
                        </Stack>

                        <Stack spacing={4}>
                            <Stack>
                                {submissions.length > 0 && (
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <AlertIcon kind={'error'} />
                                        <Text variant={'p2'}>
                                            Some fields have been disabled as some students have already submitted their
                                            evaluations.
                                        </Text>
                                    </Stack>
                                )}
                                {isLTI && (
                                    <Stack alignItems={'center'} direction={'row'} spacing={23}>
                                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                            <Text variant={'p2'}>Link to</Text>
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'} spacing={5}>
                                            <Select
                                                disabled={submissions?.length > 0}
                                                displayEmpty
                                                value={form?.[FORM_KEYS.LTI_ASSIGNMENT] || ''}
                                                error={errors?.[FORM_KEYS.LTI_ASSIGNMENT]}
                                                onChange={(e) => handleInputs(FORM_KEYS.LTI_ASSIGNMENT, e.target.value)}
                                                required
                                            >
                                                {renderLTIAssignmentsMenuItem()}
                                            </Select>
                                        </Stack>
                                    </Stack>
                                )}
                                <Stack
                                    alignItems={'center'}
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                    spacing={23}
                                >
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <Text variant={'p2'} weight={'medium'}>
                                            Format
                                        </Text>
                                        <Tooltip
                                            maxWidth={160}
                                            title={
                                                <ToolTipStack divider={<Divider />} spacing={1}>
                                                    <Text color={'inherit'} variant={'p3'}>
                                                        Format
                                                    </Text>
                                                    <Stack>
                                                        <Text color={'inherit'} variant={'p3'}>
                                                            Rubric: An assessment tool detailing the expectations of the
                                                            assignment by listing criteria and ratings to satisfy each
                                                            criterion.
                                                        </Text>
                                                        <Text color={'inherit'} variant={'p3'}>
                                                            Zero-Sum: Given 100 points, students will assign points to
                                                            their team members based on their contribution. The total
                                                            points assigned cannot exceed 100.
                                                        </Text>
                                                    </Stack>
                                                </ToolTipStack>
                                            }
                                            type={'info'}
                                        />
                                    </Stack>
                                    <ToolTipButton
                                        variant={'tertiary'}
                                        onClick={() => setToolTipDialog({ open: true })}
                                    >
                                        Customize Tooltip
                                    </ToolTipButton>
                                </Stack>
                                <Stack alignItems={'center'} direction={'row'} spacing={5}>
                                    <Select
                                        data-cy={'peer-evalutaion-menu-dropdown'}
                                        disabled={submissions?.length > 0}
                                        value={form?.[FORM_KEYS.FORMAT] || ''}
                                        error={errors?.[FORM_KEYS.FORMAT]}
                                        onChange={(e) => handleInputs(FORM_KEYS.FORMAT, e.target.value)}
                                        required
                                    >
                                        <MenuItem data-cy={'rubric-format'} value={'rubric'}>
                                            Rubric
                                        </MenuItem>
                                        <MenuItem data-cy={'zero-sum-format'} value={'zero-sum'}>
                                            Zero-Sum Game
                                        </MenuItem>
                                    </Select>
                                </Stack>
                            </Stack>
                            {form?.[FORM_KEYS.FORMAT] === 'rubric' && (
                                <Stack>
                                    <RubricTableWrapper
                                        disabled={submissions?.length > 0}
                                        device={device}
                                        data={rubricData}
                                        setData={setRubricData}
                                        errors={errors?.[FORM_KEYS.RUBRIC_DATA]}
                                    />
                                </Stack>
                            )}

                            <Stack spacing={2}>
                                <Text variant={'p2'} weight={'medium'}>
                                    Available
                                </Text>
                                <Stack spacing={4}>
                                    <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                        <CustomDatePicker
                                            data-cy={'datepicker-start'}
                                            allowTimeSelection
                                            forceTimeSelection
                                            disabledDate={(date) => date.isSameOrBefore(moment().subtract(1, 'day'))}
                                            error={errors[FORM_KEYS.START_ON]}
                                            value={form?.[FORM_KEYS.START_ON]}
                                            onChange={(value) => handleInputs(FORM_KEYS.START_ON, value)}
                                        />
                                        <Text variant={'p2'}>until</Text>
                                    </Stack>
                                    <CustomDatePicker
                                        data-cy={'datepicker-end'}
                                        allowTimeSelection
                                        forceTimeSelection
                                        disabledDate={(date) => date.isSameOrBefore(moment().subtract(1, 'day'))}
                                        error={errors[FORM_KEYS.END_ON]}
                                        value={form?.[FORM_KEYS.END_ON]}
                                        onChange={(value) => handleInputs(FORM_KEYS.END_ON, value)}
                                    />
                                </Stack>
                            </Stack>

                            <Stack spacing={2}>
                                <Text variant={'p2'} weight={'medium'}>
                                    Due
                                </Text>
                                <CustomDatePicker
                                    data-cy={'datepicker-due'}
                                    allowTimeSelection
                                    forceTimeSelection
                                    disabledDate={(date) =>
                                        !date.isBetween(
                                            moment(form?.[FORM_KEYS.START_ON]),
                                            moment(form?.[FORM_KEYS.END_ON]),
                                            'day',
                                            '[]'
                                        )
                                    }
                                    error={errors[FORM_KEYS.DUE_ON]}
                                    value={form?.[FORM_KEYS.DUE_ON]}
                                    onChange={(value) => handleInputs(FORM_KEYS.DUE_ON, value)}
                                />
                            </Stack>

                            <Stack spacing={2}>
                                <Text variant={'p2'} weight={'medium'}>
                                    Details
                                </Text>
                                <RichTextArea
                                    error={errors[FORM_KEYS.DETAILS]}
                                    placeholder={'Enter Peer Evaluation Details'}
                                    value={form?.[FORM_KEYS.DETAILS]}
                                    onChange={(html) => handleInputs(FORM_KEYS.DETAILS, html)}
                                />
                            </Stack>

                            <Stack sx={{ alignItems: 'flex-start' }}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2} sx={{ paddingTop: 2 }}>
                                    <Text variant={'p2'}>Comments</Text>
                                    <Tooltip
                                        maxWidth={160}
                                        title={
                                            <ToolTipStack divider={<Divider />} spacing={1}>
                                                <Text color={'inherit'} variant={'p3'}>
                                                    Require Comments
                                                </Text>
                                                <Stack>
                                                    <Text color={'inherit'} variant={'p3'}>
                                                        Require students to write explanations for grades given.
                                                    </Text>
                                                </Stack>
                                            </ToolTipStack>
                                        }
                                        type={'info'}
                                    ></Tooltip>
                                </Stack>
                                <Stack alignItems={'flex-start'}>
                                    <StyledCheckbox
                                        defaultChecked={form?.[FORM_KEYS.IS_COMMENT_REQURIED]}
                                        label={'Require students to justify grades given'}
                                        onChange={(e, value) => handleInputs(FORM_KEYS.IS_COMMENT_REQURIED, value)}
                                    />
                                    <Stack alignItems={'center'} direction={'row'} spacing={3} sx={{ paddingLeft: 11 }}>
                                        <Text color={'light'} variant={'p2'}>
                                            Character minimum of
                                        </Text>
                                        <FeedbackMinNumberTextInput
                                            disabled={!form?.[FORM_KEYS.IS_COMMENT_REQURIED]}
                                            value={form?.[FORM_KEYS.MIN_FEEDBACK_LENGTH]}
                                            error={errors[FORM_KEYS.MIN_FEEDBACK_LENGTH]}
                                            placeholder={'ex. 200'}
                                            onChange={(e) =>
                                                handleInputs(FORM_KEYS.MIN_FEEDBACK_LENGTH, e.target.value)
                                            }
                                        />
                                        <Text color={'light'} variant={'p2'}>
                                            Characters
                                        </Text>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>

                        <Divider spacing={7} />

                        <Stack spacing={4}>
                            <Text variant={'h6'}>Submission Results</Text>
                            <Stack spacing={4}>
                                <Stack alignItems={'center'} direction={'row'}>
                                    <StyledCheckbox
                                        defaultChecked={form?.[FORM_KEYS.RELEASE_TO_STUDENTS]}
                                        label={'Release Results to Students'}
                                        onChange={(e, value) => handleInputs(FORM_KEYS.RELEASE_TO_STUDENTS, value)}
                                    />
                                </Stack>
                                {form?.[FORM_KEYS.RELEASE_TO_STUDENTS] && (
                                    <ReleaseResultDetialStack spacing={4}>
                                        <Stack sx={{ paddingLeft: 2 }}>
                                            <Text color={'light'} variant={'p2'}>
                                                Release on
                                            </Text>
                                            <CustomDatePicker
                                                allowTimeSelection
                                                forceTimeSelection
                                                disabledDate={(date) =>
                                                    date.isSameOrBefore(
                                                        moment(form?.[FORM_KEYS.END_ON]).subtract(1, 'day')
                                                    )
                                                }
                                                error={errors[FORM_KEYS.RELEASE_ON]}
                                                value={form?.[FORM_KEYS.RELEASE_ON]}
                                                onChange={(value) => handleInputs(FORM_KEYS.RELEASE_ON, value)}
                                            />
                                        </Stack>
                                        <Stack alignItems={'center'} direction={'row'}>
                                            <StyledCheckbox
                                                defaultChecked={form?.[FORM_KEYS.KEEP_RESULT_ANONYMOUS]}
                                                label={'Keep Results Anonymous'}
                                                onChange={(e, value) =>
                                                    handleInputs(FORM_KEYS.KEEP_RESULT_ANONYMOUS, value)
                                                }
                                            />
                                        </Stack>
                                    </ReleaseResultDetialStack>
                                )}
                            </Stack>
                        </Stack>
                        {device !== 'desktop' && <Divider spacing={7} />}
                    </FormStack>
                </MainBox>
                <UtilStack alignItems={'flex-start'} device={device}>
                    <Text variant={'sh2'}>Resources</Text>
                    <ResourceSection
                        disabled={false}
                        isInstructor={true}
                        resourceList={form?.[FORM_KEYS.RESOURCES]}
                        currentAccountId={auth.owner_account_id}
                        deleteFile={(id) => handleDeleteFile(id)}
                    />
                    <Button
                        startIcon={<Icon kind={'add'} />}
                        variant={'tertiary'}
                        onClick={() => setResourcesModal({ isOpen: true })}
                    >
                        Add Resources
                    </Button>
                </UtilStack>
            </DesktopBox>
            <StickyBottomStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Button variant={'tertiary'} onClick={() => handleBackButton()}>
                    Cancel
                </Button>
                <Stack direction={'row'} spacing={7}>
                    <SplitButton
                        disabled={buttonLoading}
                        options={getSplitButtons(form, !hasChange)}
                        onClick={handleSplitButton}
                    />
                    <Dropdown
                        aria-label={'more options'}
                        id={'dropdown-2'}
                        options={renderOptionDropDown(device, form)}
                        rootComponent={<IconButton kind={'moreHorizontal'} size={'small'} variant={'square'} />}
                        onSelect={handleDropDown}
                    />
                </Stack>
            </StickyBottomStack>
        </OutterBox>
    );
};

const OutterBox = styled(Box)(({ theme }) => {
    return {
        padding: theme.spacing(0, 0, 15, 0),
        minHeight: 'calc(100vh - 64px - 20px - 58px)',
        background: theme.palette.ensightful.shades[0],
    };
});

const DesktopBox = styled(Box)(({ device }) => {
    return {
        display: device === 'desktop' ? 'grid' : 'block',
        gridTemplateColumns: '75% 25%',
        borderRadius: '12px 12px 0 0',
        width: '100%',
        height: '100%',
    };
});

const ToolTipButton = styled(Button)(() => {
    return {
        border: 'none',
    };
});

const MainBox = styled(Box)(({ device, theme }) => {
    return {
        padding: device === 'desktop' ? theme.spacing(8, 10) : theme.spacing(5, 5, 0, 5),
        borderRight: device === 'desktop' ? `1px solid ${theme.palette.grey[300]}` : 'none',
    };
});

const FormStack = styled(Stack)(() => {
    return {};
});

const UtilStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5),
        width: '100%',
    };
});

const ToolTipStack = styled(Stack)(() => {
    return {};
});

const TitleTextInput = styled(TextInput)(({ theme }) => {
    return {
        margin: theme.spacing(0, -3),
        width: `80%`,
        '&& input': {
            fontSize: `${theme.typography.sh1.fontSize.large}rem`,
            fontWeight: theme.typography.sh1.fontWeight.semibold,
            letterSpacing: `${theme.typography.sh1.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('sh1', 'large')}rem`,
        },
        '& fieldset': {
            border: 'none',
        },
    };
});

const CustomDatePicker = styled(DatePicker)(({ theme, error }) => {
    return {
        width: '200px',
        ...(error && {
            '&& fieldset': {
                borderColor: `${theme.palette.ensightful.error[700]} !important`,
            },
        }),
    };
});

const FeedbackMinNumberTextInput = styled(TextInput)(() => {
    return {
        width: '80px',
    };
});

const ReleaseResultDetialStack = styled(Stack)(({ theme }) => {
    return {
        paddingLeft: theme.spacing(4),
    };
});

const StyledCheckbox = styled(Checkbox)(({ theme }) => {
    return {
        '& span': {
            fontSize: 14,
            lineHeight: theme.spacing(4),
            color: theme.palette.ensightful.neutral[700],
        },
    };
});

const StickyBottomStack = styled(Stack)(({ theme }) => {
    return {
        zIndex: 2,
        padding: theme.spacing(0, 10),
        position: 'fixed',
        left: 0,
        bottom: 0,
        width: '100%',
        height: theme.spacing(15),
        backgroundColor: theme.palette.ensightful.shades[0],
        borderTop: `solid 1px ${theme.palette.ensightful.neutral[300]}`,
    };
});

export default CreatePeerEvaluationMobile;
